import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Search,
  Filter,
  Download,
  Star,
  ArrowRight,
  Settings,
  Database,
  Calendar,
  MessageSquare,
  Phone,
  Mail,
  CreditCard,
  Users,
  FileText,
  BarChart3,
  Workflow,
  Cloud,
  Lock,
  RefreshCw,
  Eye,
  Bell,
  PieChart,
  LineChart,
  Activity,
  Zap,
  Shield,
  Globe,
  Clock,
  TrendingUp,
  Award,
  Building2,
  Target,
  Heart,
  Crown,
  Rocket
} from 'lucide-react';

const AddOns: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('any');
  const [searchTerm, setSearchTerm] = useState<string>('');

  const categories = [
    { id: 'any', name: 'Any' },
    { id: 'marketing', name: 'Marketing Automation' },
    { id: 'productivity', name: 'Productivity' },
    { id: 'sales', name: 'Sales & CRM' },
    { id: 'analytics', name: 'Analytics & Reporting' },
    { id: 'integration', name: 'Integration' },
    { id: 'security', name: 'Security & Compliance' }
  ];

  const addOns = [
    {
      id: 'power-autonumber',
      name: 'PowerAutoNumber',
      description: 'Automatically inserts numbers, letters, dates, times, or field values into any field within Microsoft Dynamics 365.',
      category: 'productivity',
      icon: FileText,
      rating: 4.8,
      downloads: 1250,
      price: 'Free Trial',
      features: ['Auto-numbering', 'Custom patterns', 'Date/time stamps', 'Field value insertion']
    },
    {
      id: 'power-bulkdelete',
      name: 'PowerBulkDelete',
      description: 'Quickly deploys bulk delete rules for predefined D365 entities from a single page.',
      category: 'productivity',
      icon: RefreshCw,
      rating: 4.6,
      downloads: 890,
      price: 'Free Trial',
      features: ['Bulk operations', 'Entity management', 'Rule deployment', 'Data cleanup']
    },
    {
      id: 'power-calendar',
      name: 'PowerCalendar',
      description: 'Views team tasks, appointments, and phone calls on one calendar inside Dynamics 365.',
      category: 'productivity',
      icon: Calendar,
      rating: 4.7,
      downloads: 2100,
      price: 'Free Trial',
      features: ['Team calendar', 'Task management', 'Appointment tracking', 'Phone call logs']
    },
    {
      id: 'power-charge',
      name: 'PowerCharge',
      description: 'Securely stores and processes credit cards within Dynamics 365.',
      category: 'security',
      icon: CreditCard,
      rating: 4.9,
      downloads: 750,
      price: 'Free Trial',
      features: ['Secure storage', 'PCI compliance', 'Payment processing', 'Transaction tracking']
    },
    {
      id: 'power-chat',
      name: 'PowerChat',
      description: 'Enables live chat and tracks conversations from website visitors within Microsoft Dynamics 365.',
      category: 'marketing',
      icon: MessageSquare,
      rating: 4.5,
      downloads: 1650,
      price: 'Free Trial',
      features: ['Live chat', 'Visitor tracking', 'Conversation history', 'Lead generation']
    },
    {
      id: 'power-cti',
      name: 'PowerCTI',
      description: 'Automatically opens a caller\'s record within Dynamics 365 when a phone call is received.',
      category: 'sales',
      icon: Phone,
      rating: 4.8,
      downloads: 980,
      price: 'Free Trial',
      features: ['Call tracking', 'Auto-record opening', 'Caller identification', 'Integration']
    },
    {
      id: 'power-email',
      name: 'PowerEmail',
      description: 'Easily tracks opens and URL clicks of emails sent from Dynamics 365.',
      category: 'marketing',
      icon: Mail,
      rating: 4.7,
      downloads: 3200,
      price: 'Free Trial',
      features: ['Email tracking', 'Open rates', 'Click tracking', 'Campaign analytics']
    },
    {
      id: 'power-globalsearch',
      name: 'PowerGlobalSearch',
      description: 'Searches across any entity and field instantly, displaying results in a convenient and configurable reading pane.',
      category: 'productivity',
      icon: Search,
      rating: 4.9,
      downloads: 4500,
      price: 'Free Trial',
      features: ['Global search', 'Entity search', 'Configurable results', 'Reading pane']
    },
    {
      id: 'power-grid',
      name: 'PowerGrid',
      description: 'Searches for and edits records inline within Dynamics 365.',
      category: 'productivity',
      icon: BarChart3,
      rating: 4.6,
      downloads: 1800,
      price: 'Free Trial',
      features: ['Inline editing', 'Grid view', 'Search functionality', 'Record management']
    },
    {
      id: 'power-analytics',
      name: 'PowerAnalytics',
      description: 'Advanced analytics and reporting tools for Dynamics 365 data visualization.',
      category: 'analytics',
      icon: PieChart,
      rating: 4.8,
      downloads: 1200,
      price: 'Free Trial',
      features: ['Data visualization', 'Custom reports', 'Real-time dashboards', 'Export capabilities']
    },
    {
      id: 'power-workflow',
      name: 'PowerWorkflow',
      description: 'Automated workflow management and business process automation for Dynamics 365.',
      category: 'productivity',
      icon: Workflow,
      rating: 4.7,
      downloads: 950,
      price: 'Free Trial',
      features: ['Process automation', 'Workflow management', 'Business rules', 'Approval workflows']
    },
    {
      id: 'power-integration',
      name: 'PowerIntegration',
      description: 'Seamless integration with external systems and third-party applications.',
      category: 'integration',
      icon: Cloud,
      rating: 4.6,
      downloads: 1100,
      price: 'Free Trial',
      features: ['API integration', 'Data synchronization', 'Third-party apps', 'Real-time sync']
    }
  ];

  const filteredAddOns = addOns.filter(addon => {
    const matchesCategory = selectedCategory === 'any' || addon.category === selectedCategory;
    const matchesSearch = addon.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         addon.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="hero-gradient particle-bg relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="absolute right-0 top-0 w-1/3 h-full">
          <div className="absolute top-20 right-20 w-32 h-32 bg-white/10 rounded-full"></div>
          <div className="absolute top-40 right-40 w-24 h-24 bg-white/5 rounded-full"></div>
          <div className="absolute bottom-20 right-10 w-40 h-40 bg-white/10 rounded-full"></div>
        </div>
        <div className="container-custom relative z-10 py-12">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
                         <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
               <span className="gradient-text">PowerApps Add-Ons</span>
             </h1>
             <p className="text-2xl text-black/90 mb-8">
               Easily extend the capabilities of your Dynamics 365 investment!
             </p>
             <div className="max-w-4xl mx-auto space-y-6 text-black/90 text-lg leading-relaxed">
              <p>
                CRMONCE's Microsoft Business Applications Practice continuously develops these innovative, 
                intuitive, and instrumental tools based on customer feedback to help maximize your Microsoft investment.
              </p>
              <p>
                PowerPacks can be downloaded for free with 30-day trials, and PowerSuccess subscribers get unlimited use.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Catalog Section */}
      <section className="py-12 bg-white/95 backdrop-blur-xl">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
                         <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
               <span className="gradient-text">Add-Ons Catalog</span>
             </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              While add-ons work together, they need to be individually imported into Dynamics 365, 
              allowing you to select only what benefits your organization.
            </p>
          </motion.div>

          {/* Filter Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <div className="flex flex-col md:flex-row gap-6 items-center justify-between bg-gray-50 rounded-2xl p-6">
              <div className="flex flex-col md:flex-row gap-4 items-center">
                <label className="text-gray-700 font-medium">Select a category:</label>
                <div className="flex flex-wrap gap-3">
                  {categories.map((category) => (
                    <label key={category.id} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="category"
                        value={category.id}
                        checked={selectedCategory === category.id}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                        className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 focus:ring-purple-500"
                      />
                      <span className="text-gray-700">{category.name}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search add-ons..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>
          </motion.div>

          {/* Add-ons Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredAddOns.map((addon, index) => (
              <motion.div
                key={addon.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.8 }}
                className="bg-white border border-gray-200 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
              >
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <addon.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{addon.name}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">
                    {addon.description}
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center space-x-1">
                      <Star className="w-4 h-4 text-yellow-400 fill-current" />
                      <span className="text-gray-700">{addon.rating}</span>
                    </div>
                    <div className="flex items-center space-x-1 text-gray-500">
                      <Download className="w-4 h-4" />
                      <span>{addon.downloads.toLocaleString()}</span>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {addon.features.slice(0, 2).map((feature, featureIndex) => (
                      <span
                        key={featureIndex}
                        className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <span className="text-purple-600 font-semibold">{addon.price}</span>
                    <button className="text-purple-600 hover:text-purple-700 font-medium text-sm flex items-center space-x-1 transition-colors">
                      <span>Learn More</span>
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {filteredAddOns.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-12"
            >
              <div className="text-gray-500 text-lg">
                No add-ons found matching your criteria.
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 bg-gradient-to-r from-primary-600 to-accent-600">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">
              Ready to Enhance Your Dynamics 365?
            </h2>
            <p className="text-xl text-black/90 mb-8 max-w-2xl mx-auto">
              Start with a free 30-day trial of any PowerPack add-on and see how it transforms your business processes.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-accent text-lg px-8 py-4"
              >
                Start Free Trial
                <ArrowRight className="ml-2 w-5 h-5 inline" />
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-outline text-lg px-8 py-4"
              >
                Contact Sales
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AddOns; 